import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect, Route, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, ListItem, ListItemText, Tab, Tabs } from '@material-ui/core';
import {
  ApiParams,
  PageTemplateHeader,
  PageTemplateHeaderActions,
  PageTemplateNav
} from '@sierralabs/react-material-ui';

import HeaderBackButton, {
  StyledPageTemplate as StyledPageTemplateHeader
} from '../../common/HeaderBackButton';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import TaskStatusActions from '../tasks/TaskStatusActions';
import HazardBoardTrace from './board/HazardBoardTrace';
import ManageHazardTemplateCard from './cards/HazardTemplateCard';
import HazardBoardFilter, {
  HazardFilterOptions
} from './components/HazardBoardFilter';
import HazardHeaderTitle from './components/HazardHeaderTitle';
import {
  useFindHazard,
  useFindHazardVersion,
  useHazardFilterQueryParams
} from './Hazard.hooks';
import { Hazard, HazardVersion } from './Hazard.interface';
import { getHazardIdPrefix } from './Hazard.config';
import HazardDetailPage from './HazardDetailPage';
import SearchBox from '../../common/SearchBox';
import CausesTable from './tables/CausesTable';
import ControlsTable from './tables/ControlsTable';
import ActionsTable from './tables/ActionsTable';
import ListMoreButtonMenu from '../../common/ListMoreButtonMenu';
import { useFindProduct } from '../products/Product.hooks';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import { formatDate } from '../../common/formatter';
import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';

type PageType =
  | 'detail'
  | 'settings'
  | 'causes'
  | 'controls'
  | 'actions'
  | 'board'
  | 'swimlane'
  | 'trace';

const hazardEntityName = {
  causes: 'Cause',
  controls: 'Control',
  actions: 'Action'
} as const;

const StyledPageTemplate = styled(StyledPageTemplateHeader as React.FC)`
  .page-template-dashboard.board-view {
    padding: 0;
  }
  .export-button-container {
    margin-left: 10px;
  }
`;

const HazardPage: React.FC = () => {
  const history = useHistory();
  const {
    view = '',
    productId: paramProductId = '',
    hazardId: paramHazardId = ''
  } = useParams<{
    view?: PageType;
    productId: string;
    hazardId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const hazardId: number = parseInt(paramHazardId) || 0;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const filter = useHazardFilterQueryParams();
  const [version] = useQueryParam('ver', withDefault(NumberParam, 0));
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [error, setError] = useState<FetchError>();
  const [hazard, setHazard] = useState<Hazard | undefined>();
  const [hazardVersion, setHazardVersion] = useState<HazardVersion>();
  const [apiParams, setApiParams] = useState<ApiParams>({
    search,
    ...filter.getParams()
  });
  const { hazard: foundHazard } = useFindHazard(hazardId, !!view);
  const { product: foundProduct } = useFindProduct(productId, !!view);
  const {
    hazardVersion: loadedHazardVersion,
    error: findVersionError,
    findHazardVersion
  } = useFindHazardVersion(hazard?.id || 0, version);
  const isDetail = view === 'detail';
  const disabled = !!hazardVersion;

  const isDetailPage: boolean = useMemo(() => {
    return ['detail', 'settings', 'causes', 'controls', 'actions'].includes(
      view
    );
  }, [view]);

  const getPageUrl = (page: PageType) => {
    return `/portal/products/${productId}/hazards/${hazardId}/${page}`;
  };

  const gotoPage = (page: PageType) => {
    history.push(`/portal/products/${productId}/hazards/${hazardId}/${page}`);
  };

  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (foundHazard) {
      setHazard(foundHazard);
    }
  }, [foundHazard]);

  useEffect(() => {
    const params: ApiParams = { ...apiParams, search };
    !search && delete params.search;
    if (search || apiParams.search) {
      setApiParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setApiParams({ search, ...filter.getParams() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.getParams]);

  useEffect(() => {
    if (version && isDetail && hazard?.id) {
      findHazardVersion();
    } else {
      setHazardVersion(undefined);
    }
  }, [version, isDetail, hazard?.id, findHazardVersion]);

  useEffect(() => {
    setHazardVersion(loadedHazardVersion);
  }, [loadedHazardVersion]);

  useEffect(() => {
    setError(findVersionError);
  }, [findVersionError]);

  if (
    ![
      'detail',
      'settings',
      'causes',
      'controls',
      'actions',
      'board',
      'swimlane',
      'trace'
    ].includes(view)
  ) {
    return (
      <Redirect
        to={`/portal/products/${productId}/hazards/${hazardId}/detail`}
      />
    );
  }

  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/hazard`} />
        <HazardHeaderTitle
          hazard={hazard}
          disabled={disabled}
          onChange={(updatedHazard: Partial<Hazard>) => {
            hazard && setHazard({ ...hazard, ...updatedHazard });
          }}
        >
          <h2 className='title' title={hazard && 'Click to edit'}>
            {hazard?.title
              ? `${foundProduct?.name} ${getHazardIdPrefix()}-${hazardId} ${
                  hazard?.title
                }`
              : 'Loading...'}
          </h2>
        </HazardHeaderTitle>

        {isDetail ? (
          <div style={{ flex: 1 }} />
        ) : (
          <>
            <SearchBox value={search} onSearch={setSearch}>
              <HazardBoardFilter
                options={filter.options}
                onFilter={(options: HazardFilterOptions) => {
                  const params: ApiParams =
                    filter.convertOptionsToParams(options);
                  const { search } = apiParams;
                  search && (params.search = search);
                  filter.setStatus(params.status);
                  filter.setAssigneeId(params.assigneeId);
                  filter.setAuthorId(params.authorId);
                  filter.setAssignedForReviewOnly(params.assignedForReviewOnly);
                  setApiParams(params);
                }}
              />
            </SearchBox>
          </>
        )}
        <PageTemplateHeaderActions>
          <Route
            exact
            path='/portal/products/:productId/hazards/:hazardId/detail'
          >
            {!disabled && hazard?.task && (
              <TaskStatusActions
                task={hazard.task}
                taskEntity={hazard}
                onChange={task => {
                  hazard.task = task;
                  setHazard({ ...hazard });
                }}
              />
            )}
          </Route>
          {!isDetailPage && (
            <ListMoreButtonMenu>
              <ListItem
                button
                onClick={() => {
                  if (apiParams.includeDeleted) {
                    delete apiParams.includeDeleted;
                  } else {
                    apiParams.includeDeleted = true;
                  }
                  setApiParams({ ...apiParams });
                }}
              >
                <ListItemText
                  primary={`${
                    apiParams.includeDeleted ? 'Hide' : 'Show'
                  } Deleted`}
                />
              </ListItem>
            </ListMoreButtonMenu>
          )}
          {(view === 'causes' || view === 'controls' || view === 'actions') && (
            <Button
              variant='contained'
              color='primary'
              onClick={() => setOpenDialog(true)}
            >
              Add {hazardEntityName[view]}
            </Button>
          )}

          {/* TODO add export button */}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {isDetailPage && (
        <PageTemplateNav>
          <Tabs
            value={view}
            variant='scrollable'
            scrollButtons='auto'
            indicatorColor='primary'
          >
            <Tab
              component={Link}
              label='Details'
              value='detail'
              to={getPageUrl('detail')}
            />
            {/* <Tab
              component={Link}
              label='Settings'
              value='settings'
              to={getPageUrl('settings')}
            /> */}
            {/* <Tab
              component={Link}
              label='Board'
              value='board'
              to={getPageUrl('board')}
            /> */}
            <Tab
              component={Link}
              label='Hazard Board'
              value='trace'
              to={getPageUrl('trace')}
            />
            <Tab
              component={Link}
              label='Causes'
              value='causes'
              to={getPageUrl('causes')}
            />
            <Tab
              component={Link}
              label='Controls'
              value='controls'
              to={getPageUrl('controls')}
            />
            <Tab
              component={Link}
              label='Actions'
              value='actions'
              to={getPageUrl('actions')}
            />
          </Tabs>
        </PageTemplateNav>
      )}
      {hazard && !!version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {hazardVersion && ` from ${formatDate(hazardVersion.created)}`}.{' '}
          <Link to={getPageUrl('detail')}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard
        innerRef={scrollRef}
        className={!isDetailPage ? 'board-view' : ''}
      >
        {hazard && (
          <>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/detail'
            >
              <HazardDetailPage
                hazard={hazard}
                productId={productId}
                hazardVersion={hazardVersion}
                onError={setError}
                onChange={updated => {
                  setHazard(hazard => hazard && { ...hazard, ...updated });
                }}
              />
            </Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/settings'
            >
              <ManageHazardTemplateCard
                hazard={hazard}
                onChange={(updatedHazard: Partial<Hazard>) => {
                  hazard && setHazard({ ...hazard, ...updatedHazard });
                }}
              />
            </Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/causes'
            >
              <CausesTable
                onChange={() => setOpenDialog(false)}
                open={openDialog}
                hazardId={hazard.id}
                productId={productId}
                apiParams={apiParams}
              />
            </Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/controls'
            >
              <ControlsTable
                onChange={() => setOpenDialog(false)}
                open={openDialog}
                hazardId={hazard.id}
                productId={productId}
                apiParams={apiParams}
              />
            </Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/actions'
            >
              <ActionsTable
                onChange={() => setOpenDialog(false)}
                open={openDialog}
                hazardId={hazard.id}
                productId={productId}
                apiParams={apiParams}
              />
            </Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/board'
            ></Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/swimlane'
            ></Route>
            <Route
              exact
              path='/portal/products/:productId/hazards/:hazardId/trace'
            >
              <HazardBoardTrace
                hazard={hazard}
                productId={productId}
                apiParams={apiParams}
              />
            </Route>
          </>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message || 'An error occurred.'}
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default HazardPage;
